
import { useState, useEffect, useCallback } from 'react'
import { lumi } from '../lib/lumi'
import toast from 'react-hot-toast'

interface DiarioObra {
  _id: string
  obraId: string
  nomeObra: string
  numeroContrato: string
  clienteNome: string
  centroCusto: string
  tipo: 'diario' | 'semanal'
  data: string
  responsavelId: string
  responsavelNome: string
  local: string
  clima: any
  observacoes: string
  checklistSeguranca: any[]
  maoObra: any[]
  equipamentos: any[]
  atividadesManuais: any[]
  atividadesPlanejamento: any[]
  naoConformidades: any[]
  fotos: any[]
  documentos: any[]
  anexos: any[]
  medicaoAtividades: any[]
  medicaoEtapa: any
  materiaisUtilizados: any[]
  progressoGeral: number
  valorMedicao: number
  projecaoRecebimento: number
  projecaoPagamento: number
  status: 'rascunho' | 'finalizado' | 'aprovado'
  // 🔥 NOVOS CAMPOS PARA INTEGRAÇÃO COM ORÇAMENTO E PLANEJAMENTO
  orcamentoId?: string
  valoresEtapaOrcamento?: {
    etapaId: string
    etapaNome: string
    valorTotal: number
    discriminacao: {
      material: number
      equipamento: number
      maoObra: number
      composicao: number
    }
  }
  valoresPlanejamento?: {
    etapaId: string
    valorMedido: number
    percentualConcluido: number
  }[]
  createdAt: string
  updatedAt: string
}

interface Equipamento {
  _id: string
  nome: string
  tipo: string
  marca: string
  modelo: string
  numeroSerie: string
  status: 'disponivel' | 'em_uso' | 'manutencao'
  valorHora: number
  observacoes?: string
  createdAt: string
  updatedAt: string
}

interface Metrics {
  totalDiarios: number
  totalDiariosAnterior: number
  percentualAvanco: number
  percentualAvancoAnterior: number
  progressoMedio: number
  progressoMedioAnterior: number
  valorTotalMedicao: number
  valorTotalMedicaoAnterior: number
  previsaoFinalizacao: string
  previsaoFinalizacaoObra?: string
  obraFiltrada?: string
}

export const useDiarioObra = () => {
  const [diarios, setDiarios] = useState<DiarioObra[]>([])
  const [equipamentos, setEquipamentos] = useState<Equipamento[]>([])
  const [loading, setLoading] = useState(true)
  const [obraFiltrada, setObraFiltrada] = useState<string>('')

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      
      const [diariosData, equipamentosData] = await Promise.all([
        lumi.entities.diarios_obra.list(),
        lumi.entities.equipamentos.list()
      ])

      setDiarios(diariosData.list || [])
      setEquipamentos(equipamentosData.list || [])
    } catch (error) {
      console.error('Erro ao carregar dados:', error)
      toast.error('Erro ao carregar dados do diário de obra')
    } finally {
      setLoading(false)
    }
  }, [])

  // 🔥 BUSCAR ORÇAMENTOS APROVADOS PARA INTEGRAÇÃO
  const getOrcamentosAprovados = async () => {
    try {
      const { list } = await lumi.entities.orcamentos.list()
      return list.filter((orcamento: any) => orcamento.status === 'aprovado')
    } catch (error) {
      console.error('Erro ao buscar orçamentos aprovados:', error)
      return []
    }
  }

  // 🔥 BUSCAR VALORES DISCRIMINADOS DA ETAPA DO ORÇAMENTO
  const getValoresEtapaOrcamento = async (orcamentoId: string, etapaId: string) => {
    try {
      const { list } = await lumi.entities.orcamentos.list()
      const orcamento = list.find((o: any) => o._id === orcamentoId)
      
      if (!orcamento || !orcamento.etapas) {
        return null
      }

      // Buscar a etapa específica
      const etapa = orcamento.etapas.find((e: any) => e.codigo === etapaId)
      if (!etapa) {
        return null
      }

      // Buscar dados dos insumos para classificar por tipo
      const { list: insumos } = await lumi.entities.insumos.list()
      
      let materialTotal = 0
      let equipamentoTotal = 0
      let maoObraTotal = 0
      let composicaoTotal = 0

      // Calcular totais por categoria
      etapa.subetapas?.forEach((subetapa: any) => {
        subetapa.itens?.forEach((item: any) => {
          if (item.tipo === 'insumo') {
            const insumo = insumos.find((i: any) => i._id === item.itemId)
            if (insumo) {
              switch (insumo.tipo) {
                case 'material':
                  materialTotal += item.subtotal
                  break
                case 'equipamento':
                  equipamentoTotal += item.subtotal
                  break
                case 'mao_obra':
                  maoObraTotal += item.subtotal
                  break
                default:
                  materialTotal += item.subtotal
                  break
              }
            } else {
              materialTotal += item.subtotal
            }
          } else if (item.tipo === 'composicao') {
            composicaoTotal += item.subtotal
          }
        })
      })

      const valorTotal = materialTotal + equipamentoTotal + maoObraTotal + composicaoTotal

      return {
        etapaId: etapa.codigo,
        etapaNome: etapa.nome,
        valorTotal,
        discriminacao: {
          material: materialTotal,
          equipamento: equipamentoTotal,
          maoObra: maoObraTotal,
          composicao: composicaoTotal
        }
      }
    } catch (error) {
      console.error('Erro ao buscar valores da etapa do orçamento:', error)
      return null
    }
  }

  // 🔥 NOVA FUNÇÃO: BUSCAR VALORES MEDIDOS DAS ETAPAS DO PLANEJAMENTO
  const getValoresPlanejamento = async (obraId: string) => {
    try {
      const { list: obras } = await lumi.entities.planejamento_obras.list()
      const obra = obras.find((o: any) => o._id === obraId)
      
      if (!obra || !obra.etapas) {
        return []
      }

      return obra.etapas.map((etapa: any) => ({
        etapaId: etapa.id,
        etapaNome: etapa.nome,
        valorMedido: (etapa.valorOrcado || 0) * ((etapa.percentualConcluido || 0) / 100),
        valorOrcado: etapa.valorOrcado || 0,
        percentualConcluido: etapa.percentualConcluido || 0
      }))
    } catch (error) {
      console.error('Erro ao buscar valores do planejamento:', error)
      return []
    }
  }

  // 🔥 MÉTRICAS POR OBRA ESPECÍFICA
  const calculateMetrics = (): Metrics => {
    const now = new Date()
    const currentMonth = now.getMonth()
    const currentYear = now.getFullYear()
    
    const previousMonth = currentMonth === 0 ? 11 : currentMonth - 1
    const previousYear = currentMonth === 0 ? currentYear - 1 : currentYear

    // Filtrar diários por obra se selecionada
    const diariosConsiderados = obraFiltrada 
      ? diarios.filter(d => d.obraId === obraFiltrada)
      : diarios

    const currentMonthDiarios = diariosConsiderados.filter(diario => {
      const date = new Date(diario.createdAt)
      return date.getMonth() === currentMonth && date.getFullYear() === currentYear
    })

    const previousMonthDiarios = diariosConsiderados.filter(diario => {
      const date = new Date(diario.createdAt)
      return date.getMonth() === previousMonth && date.getFullYear() === previousYear
    })

    const totalDiarios = currentMonthDiarios.length
    const totalDiariosAnterior = previousMonthDiarios.length

    // 🔥 PERCENTUAL DE AVANÇO ESPECÍFICO DA OBRA
    const percentualAvanco = diariosConsiderados.length > 0 
      ? diariosConsiderados.reduce((acc, diario) => acc + diario.progressoGeral, 0) / diariosConsiderados.length 
      : 0

    const percentualAvancoAnterior = previousMonthDiarios.length > 0
      ? previousMonthDiarios.reduce((acc, diario) => acc + diario.progressoGeral, 0) / previousMonthDiarios.length
      : 0

    // 🔥 PROGRESSO MÉDIO ESPECÍFICO DA OBRA
    const progressoMedio = percentualAvanco
    const progressoMedioAnterior = percentualAvancoAnterior

    // 🔥 VALOR TOTAL MEDIÇÃO ESPECÍFICO DA OBRA
    const valorTotalMedicao = diariosConsiderados.reduce((acc, diario) => acc + diario.valorMedicao, 0)
    const valorTotalMedicaoAnterior = previousMonthDiarios.reduce((acc, diario) => acc + diario.valorMedicao, 0)

    // Cálculo da previsão geral
    const diasRestantes = progressoMedio > 0 ? Math.ceil((100 - progressoMedio) / (progressoMedio / 30)) : 365
    const previsaoFinalizacao = new Date(now.getTime() + diasRestantes * 24 * 60 * 60 * 1000).toISOString()

    // 🔥 PREVISÃO ESPECÍFICA DA OBRA
    let previsaoFinalizacaoObra = previsaoFinalizacao
    
    if (obraFiltrada && diariosConsiderados.length > 0) {
      // Calcular velocidade de progresso da obra específica
      const diariosOrdenados = diariosConsiderados.sort((a, b) => new Date(a.data).getTime() - new Date(b.data).getTime())
      
      if (diariosOrdenados.length >= 2) {
        const primeirosDiarios = diariosOrdenados.slice(0, Math.ceil(diariosOrdenados.length / 2))
        const ultimosDiarios = diariosOrdenados.slice(Math.floor(diariosOrdenados.length / 2))
        
        const progressoInicial = primeirosDiarios.reduce((acc, d) => acc + d.progressoGeral, 0) / primeirosDiarios.length
        const progressoRecente = ultimosDiarios.reduce((acc, d) => acc + d.progressoGeral, 0) / ultimosDiarios.length
        
        const diasDecorridos = Math.max(1, diariosOrdenados.length)
        const velocidadeProgresso = Math.max(0.1, (progressoRecente - progressoInicial) / diasDecorridos)
        
        const progressoRestante = 100 - progressoMedio
        const diasRestantesObra = Math.ceil(progressoRestante / velocidadeProgresso)
        
        previsaoFinalizacaoObra = new Date(now.getTime() + diasRestantesObra * 24 * 60 * 60 * 1000).toISOString()
      }
    }

    return {
      totalDiarios,
      totalDiariosAnterior,
      percentualAvanco,
      percentualAvancoAnterior,
      progressoMedio,
      progressoMedioAnterior,
      valorTotalMedicao,
      valorTotalMedicaoAnterior,
      previsaoFinalizacao,
      previsaoFinalizacaoObra,
      obraFiltrada
    }
  }

  // Create diário
  const createDiario = async (data: Omit<DiarioObra, '_id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const now = new Date().toISOString()
      
      // 🔥 BUSCAR VALORES DA ETAPA DO ORÇAMENTO SE FORNECIDO
      let valoresEtapaOrcamento = null
      if (data.orcamentoId && data.medicaoEtapa?.etapaId) {
        valoresEtapaOrcamento = await getValoresEtapaOrcamento(data.orcamentoId, data.medicaoEtapa.etapaId)
      }

      // 🔥 BUSCAR VALORES DO PLANEJAMENTO DA OBRA
      let valoresPlanejamento = []
      if (data.obraId) {
        valoresPlanejamento = await getValoresPlanejamento(data.obraId)
      }
      
      const diarioData = {
        ...data,
        progressoGeral: Number(data.progressoGeral || 0),
        valorMedicao: Number(data.valorMedicao || 0),
        projecaoRecebimento: Number(data.projecaoRecebimento || 0),
        projecaoPagamento: Number(data.projecaoPagamento || 0),
        valoresEtapaOrcamento,
        valoresPlanejamento,
        creator: 'admin',
        createdAt: now,
        updatedAt: now
      }

      await lumi.entities.diarios_obra.create(diarioData)

      // Integração com planejamento de obra
      if (data.medicaoEtapa && data.obraId) {
        await updatePlanejamentoObra(data.obraId, data.medicaoEtapa)
      }

      // Integração com funcionários
      if (data.maoObra && data.maoObra.length > 0) {
        await updateHistoricoFuncionarios(data.maoObra, data.obraId)
      }

      await fetchData()
      toast.success('Diário de obra criado com sucesso!')
    } catch (error) {
      console.error('Erro ao criar diário:', error)
      toast.error('Erro ao criar diário de obra')
      throw error
    }
  }

  // Update diário
  const updateDiario = async (id: string, data: Partial<DiarioObra>) => {
    try {
      const updateData = {
        ...data,
        updatedAt: new Date().toISOString()
      }

      await lumi.entities.diarios_obra.update(id, updateData)
      await fetchData()
      toast.success('Diário atualizado com sucesso!')
    } catch (error) {
      console.error('Erro ao atualizar diário:', error)
      toast.error('Erro ao atualizar diário')
      throw error
    }
  }

  // Delete diário
  const deleteDiario = async (id: string) => {
    try {
      await lumi.entities.diarios_obra.delete(id)
      await fetchData()
      toast.success('Diário excluído com sucesso!')
    } catch (error) {
      console.error('Erro ao excluir diário:', error)
      toast.error('Erro ao excluir diário')
      throw error
    }
  }

  // Create equipamento
  const createEquipamento = async (data: Omit<Equipamento, '_id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const now = new Date().toISOString()
      
      const equipamentoData = {
        ...data,
        valorHora: Number(data.valorHora || 0),
        creator: 'admin',
        createdAt: now,
        updatedAt: now
      }

      const newEquipamento = await lumi.entities.equipamentos.create(equipamentoData)
      await fetchData()
      toast.success('Equipamento criado com sucesso!')
      return newEquipamento
    } catch (error) {
      console.error('Erro ao criar equipamento:', error)
      toast.error('Erro ao criar equipamento')
      throw error
    }
  }

  // 🔥 NOVA FUNÇÃO: ATUALIZAR ATIVIDADE DO PLANEJAMENTO
  const updateAtividade = async (atividadeId: string, updates: any) => {
    try {
      await lumi.entities.atividades_obra.update(atividadeId, {
        ...updates,
        updatedAt: new Date().toISOString()
      })
      
      toast.success('Status da atividade atualizado!')
    } catch (error) {
      console.error('Erro ao atualizar atividade:', error)
      toast.error('Erro ao atualizar status da atividade')
      throw error
    }
  }

  // 🔥 EXPORTAÇÃO PDF INDIVIDUAL - VERSÃO CORRIGIDA COM DOWNLOAD GARANTIDO
  const exportDiarioPDF = async (diario: DiarioObra) => {
    let toastId: string | undefined

    try {
      // Validação inicial robusta
      if (!diario || !diario._id || typeof diario !== 'object') {
        throw new Error('Dados do diário inválidos ou ausentes')
      }

      console.log('🚀 Iniciando exportação PDF para diário:', diario._id)
      toastId = toast.loading('Preparando exportação PDF...', { duration: 0 })

      // 🔥 IMPORTAÇÃO DINÂMICA ROBUSTA COM VERIFICAÇÃO COMPLETA
      let jsPDF: any
      try {
        console.log('📦 Carregando biblioteca jsPDF...')
        
        // Importação dinâmica com múltiplas tentativas
        const jsPDFModule = await import('jspdf')
        
        // Verificação robusta da classe jsPDF
        jsPDF = jsPDFModule.jsPDF || jsPDFModule.default?.jsPDF || jsPDFModule.default
        
        if (!jsPDF || typeof jsPDF !== 'function') {
          throw new Error('jsPDF não foi carregado corretamente')
        }

        console.log('✅ jsPDF carregado com sucesso')
      } catch (importError) {
        console.error('❌ Erro ao importar jsPDF:', importError)
        throw new Error('Falha ao carregar biblioteca PDF')
      }

      // Atualizar toast
      toast.loading('Gerando conteúdo PDF...', { id: toastId })

      // 🔥 CRIAÇÃO ROBUSTA DO DOCUMENTO PDF
      let doc: any
      try {
        doc = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4'
        })
        console.log('✅ Documento PDF criado')
      } catch (docError) {
        console.error('❌ Erro ao criar documento PDF:', docError)
        throw new Error('Falha ao inicializar documento PDF')
      }

      let yPos = 20

      // Função auxiliar para quebra de página
      const checkPageBreak = (requiredSpace = 20) => {
        if (yPos + requiredSpace > 280) {
          doc.addPage()
          yPos = 20
        }
      }

      // Função auxiliar para texto seguro
      const safeText = (text: any, defaultValue = 'N/A') => {
        if (text === null || text === undefined || text === '') {
          return defaultValue
        }
        return String(text).substring(0, 100)
      }

      // Formatação de moeda
      const formatCurrency = (value: any) => {
        try {
          const num = Number(value) || 0
          return num.toLocaleString('pt-BR', { 
            style: 'currency', 
            currency: 'BRL' 
          })
        } catch {
          return `R$ ${Number(value) || 0}`
        }
      }

      console.log('📝 Gerando conteúdo do PDF...')

      try {
        // CABEÇALHO
        doc.setFontSize(20)
        doc.setFont('helvetica', 'bold')
        doc.text('DIÁRIO DE OBRA', 20, yPos)
        yPos += 20
        
        doc.setFontSize(12)
        doc.setFont('helvetica', 'normal')
        doc.text(`Obra: ${safeText(diario.nomeObra)}`, 20, yPos)
        yPos += 8
        doc.text(`Contrato: ${safeText(diario.numeroContrato)}`, 20, yPos)
        yPos += 8
        
        // Data formatada
        let dataFormatada = 'N/A'
        try {
          if (diario.data) {
            const date = new Date(diario.data)
            if (!isNaN(date.getTime())) {
              dataFormatada = date.toLocaleDateString('pt-BR')
            }
          }
        } catch {
          dataFormatada = 'N/A'
        }
        
        doc.text(`Data: ${dataFormatada}`, 20, yPos)
        yPos += 8
        doc.text(`Responsável: ${safeText(diario.responsavelNome)}`, 20, yPos)
        yPos += 8
        doc.text(`Centro de Custo: ${safeText(diario.centroCusto)}`, 20, yPos)
        yPos += 15

        checkPageBreak(30)

        // PROGRESSO E MEDIÇÃO
        doc.setFont('helvetica', 'bold')
        doc.text('PROGRESSO E MEDIÇÃO:', 20, yPos)
        yPos += 10
        doc.setFont('helvetica', 'normal')
        doc.text(`Progresso Geral: ${Number(diario.progressoGeral || 0)}%`, 25, yPos)
        yPos += 8
        doc.text(`Valor da Medição: ${formatCurrency(diario.valorMedicao)}`, 25, yPos)
        yPos += 8
        doc.text(`Projeção Recebimento: ${formatCurrency(diario.projecaoRecebimento)}`, 25, yPos)
        yPos += 15

        checkPageBreak(40)

        // VALORES DISCRIMINADOS DO ORÇAMENTO
        if (diario.valoresEtapaOrcamento) {
          doc.setFont('helvetica', 'bold')
          doc.text('VALORES DA ETAPA (ORÇAMENTO):', 20, yPos)
          yPos += 10
          
          const valores = diario.valoresEtapaOrcamento
          doc.setFont('helvetica', 'normal')
          doc.text(`Etapa: ${safeText(valores.etapaNome)}`, 25, yPos)
          yPos += 8
          doc.text(`Valor Total: ${formatCurrency(valores.valorTotal)}`, 25, yPos)
          yPos += 10
          
          doc.setFont('helvetica', 'bold')
          doc.text('Discriminação:', 25, yPos)
          yPos += 8
          doc.setFont('helvetica', 'normal')
          doc.text(`Material: ${formatCurrency(valores.discriminacao?.material || 0)}`, 30, yPos)
          yPos += 6
          doc.text(`Equipamento: ${formatCurrency(valores.discriminacao?.equipamento || 0)}`, 30, yPos)
          yPos += 6
          doc.text(`Mão de Obra: ${formatCurrency(valores.discriminacao?.maoObra || 0)}`, 30, yPos)
          yPos += 6
          doc.text(`Composições: ${formatCurrency(valores.discriminacao?.composicao || 0)}`, 30, yPos)
          yPos += 15

          checkPageBreak(40)
        }

        // OBSERVAÇÕES
        if (diario.observacoes) {
          doc.setFont('helvetica', 'bold')
          doc.text('OBSERVAÇÕES:', 20, yPos)
          yPos += 10
          
          doc.setFont('helvetica', 'normal')
          try {
            const observacoesText = safeText(diario.observacoes)
            const observacoes = doc.splitTextToSize(observacoesText, 170)
            
            if (Array.isArray(observacoes)) {
              observacoes.forEach((line: string) => {
                checkPageBreak(8)
                doc.text(line, 25, yPos)
                yPos += 6
              })
            } else {
              checkPageBreak(8)
              doc.text(observacoes, 25, yPos)
              yPos += 6
            }
          } catch {
            doc.text('Observações não disponíveis', 25, yPos)
            yPos += 6
          }
        }

        console.log('✅ Conteúdo PDF gerado com sucesso')

      } catch (contentError) {
        console.error('❌ Erro ao gerar conteúdo PDF:', contentError)
        throw new Error('Falha ao gerar conteúdo PDF')
      }

      // Atualizar toast
      toast.loading('Preparando download...', { id: toastId })

      // 🔥 NOME DO ARQUIVO SEGURO
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)
      const nomeObraSeguro = safeText(diario.nomeObra, 'obra')
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .replace(/\s+/g, '-')
        .toLowerCase()
        .substring(0, 30)
      
      const fileName = `diario-obra-${nomeObraSeguro}-${timestamp}.pdf`
      console.log('📁 Nome do arquivo:', fileName)

      // 🔥 DOWNLOAD FORÇADO COM MÚLTIPLAS ESTRATÉGIAS GARANTIDAS
      let downloadSuccess = false

      try {
        console.log('📥 Tentativa 1: Download via doc.save()')
        
        // Estratégia 1: doc.save() padrão
        doc.save(fileName)
        downloadSuccess = true
        console.log('✅ Download executado via doc.save()')
        
      } catch (saveError) {
        console.warn('⚠️ doc.save() falhou, tentando método alternativo:', saveError)
        
        try {
          console.log('📥 Tentativa 2: Download via blob + link')
          
          // Estratégia 2: Download manual via blob
          const pdfOutput = doc.output('blob')
          
          if (pdfOutput && pdfOutput.size > 0) {
            const url = URL.createObjectURL(pdfOutput)
            
            const link = document.createElement('a')
            link.href = url
            link.download = fileName
            link.style.display = 'none'
            
            // Adicionar ao DOM, clicar e remover
            document.body.appendChild(link)
            link.click()
            
            // Cleanup com delay
            setTimeout(() => {
              if (document.body.contains(link)) {
                document.body.removeChild(link)
              }
              URL.revokeObjectURL(url)
            }, 100)
            
            downloadSuccess = true
            console.log('✅ Download manual executado com sucesso')
          } else {
            throw new Error('Blob PDF vazio')
          }
          
        } catch (blobError) {
          console.error('❌ Método blob falhou:', blobError)
          
          try {
            console.log('📥 Tentativa 3: Download via arraybuffer')
            
            // Estratégia 3: Download via arraybuffer
            const pdfArrayBuffer = doc.output('arraybuffer')
            const blob = new Blob([pdfArrayBuffer], { type: 'application/pdf' })
            
            if (blob.size > 0) {
              const url = URL.createObjectURL(blob)
              const link = document.createElement('a')
              link.href = url
              link.download = fileName
              link.style.display = 'none'
              
              document.body.appendChild(link)
              link.click()
              
              setTimeout(() => {
                if (document.body.contains(link)) {
                  document.body.removeChild(link)
                }
                URL.revokeObjectURL(url)
              }, 100)
              
              downloadSuccess = true
              console.log('✅ Download via arraybuffer executado')
            } else {
              throw new Error('ArrayBuffer PDF vazio')
            }
            
          } catch (arrayBufferError) {
            console.error('❌ Método arraybuffer falhou:', arrayBufferError)
            
            // 🔥 Estratégia 4: Window.open como último recurso
            try {
              console.log('📥 Tentativa 4: Abrir PDF em nova aba')
              
              const pdfDataUri = doc.output('datauristring')
              const newWindow = window.open()
              
              if (newWindow) {
                newWindow.document.write(`
                  <html>
                    <head><title>Diário de Obra - ${diario.nomeObra}</title></head>
                    <body style="margin:0;">
                      <embed width="100%" height="100%" src="${pdfDataUri}" type="application/pdf">
                    </body>
                  </html>
                `)
                downloadSuccess = true
                console.log('✅ PDF aberto em nova aba')
              } else {
                throw new Error('Popup bloqueado')
              }
              
            } catch (windowError) {
              console.error('❌ Todas as estratégias falharam:', windowError)
              throw new Error('Não foi possível executar o download')
            }
          }
        }
      }

      // Verificação final
      if (!downloadSuccess) {
        throw new Error('Download não foi executado')
      }

      // Sucesso
      if (toastId) {
        toast.success('PDF exportado e baixado com sucesso!', { id: toastId })
      }
      
      console.log('🎉 Exportação PDF concluída:', fileName)
      return fileName

    } catch (error) {
      console.error('❌ Erro na exportação PDF:', error)
      
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido'
      
      if (toastId) {
        toast.error(`Erro ao exportar PDF: ${errorMessage}`, { id: toastId })
      } else {
        toast.error(`Erro ao exportar PDF: ${errorMessage}`)
      }
      
      throw error
    }
  }

  // 🔥 EXPORTAÇÃO MÚLTIPLA DE PDFs - VERSÃO CORRIGIDA
  const exportMultiplePDFs = async (diarios: DiarioObra[]) => {
    if (!Array.isArray(diarios) || diarios.length === 0) {
      toast.error('Nenhum diário selecionado para exportação')
      return []
    }

    const maxBulkExport = 10
    if (diarios.length > maxBulkExport) {
      toast.error(`Máximo de ${maxBulkExport} diários por exportação em massa`)
      return []
    }

    let toastId: string | undefined

    try {
      console.log(`🚀 Iniciando exportação em massa de ${diarios.length} PDFs`)
      toastId = toast.loading(`Preparando exportação de ${diarios.length} PDFs...`, { 
        duration: 0 
      })
      
      const exportedFiles: string[] = []
      const failedExports: Array<{ diario: DiarioObra; error: string }> = []
      
      for (let i = 0; i < diarios.length; i++) {
        const diario = diarios[i]
        
        try {
          console.log(`📄 Exportando PDF ${i + 1} de ${diarios.length}: ${diario.nomeObra}`)
          
          toast.loading(`Exportando PDF ${i + 1} de ${diarios.length}...`, { id: toastId })
          
          if (!diario || !diario._id) {
            throw new Error('Diário inválido')
          }
          
          const fileName = await exportDiarioPDF(diario)
          
          if (fileName) {
            exportedFiles.push(fileName)
            console.log(`✅ PDF ${i + 1} exportado: ${fileName}`)
          } else {
            throw new Error('Nome do arquivo não retornado')
          }
          
          // Delay entre exportações
          if (i < diarios.length - 1) {
            const delay = Math.min(500 + (i * 100), 2000)
            await new Promise(resolve => setTimeout(resolve, delay))
          }
          
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido'
          console.error(`❌ Erro ao exportar diário ${diario._id}:`, error)
          
          failedExports.push({
            diario,
            error: errorMessage
          })
        }
      }
      
      // Resultado final
      const successCount = exportedFiles.length
      const failCount = failedExports.length
      
      if (successCount === diarios.length) {
        toast.success(`🎉 ${successCount} PDFs exportados com sucesso!`, { id: toastId })
      } else if (successCount > 0) {
        toast.success(`⚠️ ${successCount} PDFs exportados, ${failCount} falharam`, { id: toastId })
      } else {
        toast.error(`❌ Falha ao exportar todos os ${diarios.length} PDFs`, { id: toastId })
      }

      return exportedFiles
      
    } catch (error) {
      console.error('❌ Erro crítico na exportação múltipla:', error)
      
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido'
      
      if (toastId) {
        toast.error(`Erro na exportação em massa: ${errorMessage}`, { id: toastId })
      } else {
        toast.error(`Erro na exportação em massa: ${errorMessage}`)
      }
      
      throw error
    }
  }

  // 🔥 HISTÓRICO DA OBRA
  const getHistoricoObra = async (obraId: string) => {
    try {
      const { list: diariosObra } = await lumi.entities.diarios_obra.list()
      const { list: atividadesObra } = await lumi.entities.atividades_obra.list()
      const { list: mensagensObra } = await lumi.entities.mensagens_obra.list()
      
      const historico = [
        ...diariosObra
          .filter((d: any) => d.obraId === obraId)
          .map((d: any) => ({
            tipo: 'diario',
            data: d.data,
            descricao: `Diário de obra - Progresso: ${d.progressoGeral}%`,
            responsavel: d.responsavelNome,
            detalhes: d
          })),
        ...atividadesObra
          .filter((a: any) => a.obraId === obraId)
          .map((a: any) => ({
            tipo: 'atividade',
            data: a.createdAt,
            descricao: `Atividade: ${a.descricao}`,
            responsavel: a.funcionarioResponsavel,
            detalhes: a
          })),
        ...mensagensObra
          .filter((m: any) => m.obraId === obraId)
          .map((m: any) => ({
            tipo: 'mensagem',
            data: m.dataEnvio,
            descricao: `Mensagem: ${m.mensagem.substring(0, 50)}...`,
            responsavel: m.remetente,
            detalhes: m
          }))
      ].sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime())

      return historico
    } catch (error) {
      console.error('Erro ao buscar histórico:', error)
      return []
    }
  }

  // Get obras disponíveis
  const getObrasDisponiveis = async () => {
    try {
      const { list } = await lumi.entities.planejamento_obras.list()
      return list || []
    } catch (error) {
      console.error('Erro ao buscar obras:', error)
      return []
    }
  }

  // Get funcionários disponíveis
  const getFuncionariosDisponiveis = async () => {
    try {
      const { list } = await lumi.entities.funcionarios.list()
      return list.filter((funcionario: any) => funcionario.status === 'ativo') || []
    } catch (error) {
      console.error('Erro ao buscar funcionários:', error)
      return []
    }
  }

  // Get equipamentos disponíveis
  const getEquipamentosDisponiveis = async () => {
    try {
      return equipamentos.filter(eq => eq.status === 'disponivel')
    } catch (error) {
      console.error('Erro ao buscar equipamentos:', error)
      return []
    }
  }

  // 🔥 ATIVIDADES DO PLANEJAMENTO SEMANAL
  const getAtividadesPlanejamento = async (obraId: string, etapaId?: string) => {
    try {
      const { list } = await lumi.entities.atividades_obra.list()
      return list.filter((atividade: any) => {
        const matchesObra = atividade.obraId === obraId
        const matchesEtapa = !etapaId || atividade.etapaId === etapaId
        return matchesObra && matchesEtapa
      }) || []
    } catch (error) {
      console.error('Erro ao buscar atividades do planejamento:', error)
      return []
    }
  }

  // Update planejamento obra
  const updatePlanejamentoObra = async (obraId: string, medicaoEtapa: any) => {
    try {
      const { list: obras } = await lumi.entities.planejamento_obras.list()
      const obra = obras.find((o: any) => o._id === obraId)
      
      if (obra && medicaoEtapa.etapaId) {
        const etapas = obra.etapas || []
        const etapaIndex = etapas.findIndex((e: any) => e.id === medicaoEtapa.etapaId)
        
        if (etapaIndex >= 0) {
          etapas[etapaIndex].percentualConcluido = medicaoEtapa.percentualConcluido
          
          await lumi.entities.planejamento_obras.update(obraId, {
            etapas,
            updatedAt: new Date().toISOString()
          })
        }
      }
    } catch (error) {
      console.error('Erro ao atualizar planejamento:', error)
    }
  }

  // Update histórico funcionários
  const updateHistoricoFuncionarios = async (maoObra: any[], obraId: string) => {
    try {
      for (const mo of maoObra) {
        await lumi.entities.alocacoes_funcionarios.create({
          funcionarioId: mo.funcionarioId,
          obraId,
          dataInicio: new Date().toISOString(),
          dataFim: '',
          horasAlocadas: mo.horas,
          valorHora: mo.valorHora,
          valorTotal: mo.valorTotal,
          status: 'ativa',
          observacoes: 'Registro via diário de obra',
          creator: 'admin',
          createdAt: new Date().toISOString()
        })
      }
    } catch (error) {
      console.error('Erro ao atualizar histórico funcionários:', error)
    }
  }

  // 🔥 DEFINIR OBRA FILTRADA
  const setObraFiltro = (obraId: string) => {
    setObraFiltrada(obraId)
  }

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return {
    diarios,
    equipamentos,
    loading,
    metrics: calculateMetrics(),
    fetchData,
    createDiario,
    updateDiario,
    deleteDiario,
    createEquipamento,
    getObrasDisponiveis,
    getFuncionariosDisponiveis,
    getEquipamentosDisponiveis,
    getAtividadesPlanejamento,
    exportDiarioPDF, // 🔥 FUNÇÃO CORRIGIDA E ROBUSTA
    exportMultiplePDFs, // 🔥 FUNÇÃO CORRIGIDA E ROBUSTA
    getHistoricoObra,
    setObraFiltro,
    // 🔥 NOVAS FUNÇÕES PARA INTEGRAÇÃO COM ORÇAMENTO E PLANEJAMENTO
    getOrcamentosAprovados,
    getValoresEtapaOrcamento,
    getValoresPlanejamento, // 🔥 NOVA FUNÇÃO
    updateAtividade // 🔥 NOVA FUNÇÃO PARA ALTERAR STATUS
  }
}
